import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { del, get, post } from "../services/apiClient";
import { businessKeys } from "./useBusinesses";

export type EditRequestStatus =
  | "PENDING"
  | "APPROVED"
  | "REJECTED"
  | "CANCELLED";

export interface EditRequestSummary {
  id: string;
  businessId: string;
  requestedBy: string;
  changes: Record<string, unknown>;
  status: EditRequestStatus;
  rejectionReason?: string | null;
  note?: string | null;
  reviewedAt?: string | null;
  createdAt: string;
  updatedAt: string;
  business: {
    id: string;
    businessName: string;
    nameEn: string;
    city: string;
    status: string;
    owner?: { id: string; name: string; phone: string } | null;
  };
  reviewer?: { id: string; name: string; email: string } | null;
}

export interface EditRequestDetail extends EditRequestSummary {
  business: EditRequestSummary["business"] & Record<string, unknown>;
}

export interface EditRequestListResponse {
  data: EditRequestSummary[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export const editRequestKeys = {
  all: ["business-edit-requests"] as const,
  list: (params: { status?: EditRequestStatus; page: number; limit: number }) =>
    [...editRequestKeys.all, "list", params] as const,
  detail: (id: string) => [...editRequestKeys.all, "detail", id] as const,
  forBusiness: (businessId: string) =>
    [...editRequestKeys.all, "for-business", businessId] as const,
};

export const useEditRequestList = (params: {
  status?: EditRequestStatus;
  page?: number;
  limit?: number;
}) => {
  const page = params.page ?? 1;
  const limit = params.limit ?? 25;
  return useQuery<EditRequestListResponse>({
    queryKey: editRequestKeys.list({ status: params.status, page, limit }),
    queryFn: () =>
      get<EditRequestListResponse>("/business-edit-requests", {
        ...(params.status ? { status: params.status } : {}),
        page,
        limit,
      }),
  });
};

export const useEditRequest = (id: string | undefined) =>
  useQuery<EditRequestDetail>({
    queryKey: editRequestKeys.detail(id ?? ""),
    queryFn: () => get<EditRequestDetail>(`/business-edit-requests/${id}`),
    enabled: !!id,
  });

export const useBusinessPendingEditRequest = (businessId: string | undefined) =>
  useQuery<EditRequestSummary | null>({
    queryKey: editRequestKeys.forBusiness(businessId ?? ""),
    queryFn: () =>
      get<EditRequestSummary | null>(
        `/businesses/${businessId}/edit-requests/pending`,
      ),
    enabled: !!businessId,
  });

const invalidateAll = (
  qc: ReturnType<typeof useQueryClient>,
  businessId?: string,
) => {
  qc.invalidateQueries({ queryKey: editRequestKeys.all });
  if (businessId) {
    qc.invalidateQueries({ queryKey: editRequestKeys.forBusiness(businessId) });
    qc.invalidateQueries({ queryKey: businessKeys.detail(businessId) });
    qc.invalidateQueries({ queryKey: businessKeys.all });
  }
};

export const useApproveEditRequest = () => {
  const qc = useQueryClient();
  return useMutation<
    unknown,
    Error,
    { id: string; businessId: string; note?: string }
  >({
    mutationFn: ({ id, note }) =>
      post(`/business-edit-requests/${id}/approve`, { note }),
    onSuccess: (_d, v) => invalidateAll(qc, v.businessId),
  });
};

export const useRejectEditRequest = () => {
  const qc = useQueryClient();
  return useMutation<
    unknown,
    Error,
    { id: string; businessId: string; rejectionReason: string; note?: string }
  >({
    mutationFn: ({ id, rejectionReason, note }) =>
      post(`/business-edit-requests/${id}/reject`, { rejectionReason, note }),
    onSuccess: (_d, v) => invalidateAll(qc, v.businessId),
  });
};

export const useCancelEditRequest = () => {
  const qc = useQueryClient();
  return useMutation<unknown, Error, { id: string; businessId?: string }>({
    mutationFn: ({ id }) => del(`/business-edit-requests/${id}`),
    onSuccess: (_d, v) => invalidateAll(qc, v.businessId),
  });
};
